import React, { useState } from "react";
import { injectIntl, IntlShape } from "react-intl";
import { gql, useQuery } from "@apollo/client";
import MaterialTable, { Column } from "material-table";
import { Tooltip, Dialog, DialogTitle, DialogContent } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircleRounded";
import CancelIcon from "@material-ui/icons/CancelRounded";
import ScheduleIcon from "@material-ui/icons/ScheduleRounded";
import moment from "moment";
import { getSkipReasonLabels } from "../pre-season-shared/constants";
import messages from "./PageTeamPreSeason.intl";

export const PRE_SEASON_PROGRESS = gql`
  query PreSeasonProgress($exerciseId: ID!) {
    preSeasonProgress(exerciseId: $exerciseId) {
      exercise {
        id
        title
        type
      }
      dates
      rows {
        player {
          id
          name
          surname
        }
        entries {
          id
          date
          status
          reason
          reasonNote
          timeMinutes
          distanceKm
          sets
          reps
          weightKg
          note
        }
        completedCount
        notDoneCount
        pendingCount
      }
    }
  }
`;

interface IEntry {
  id: string;
  date: string;
  status: "PENDING" | "DONE" | "NOT_DONE";
  reason?: string;
  reasonNote?: string;
  timeMinutes?: number;
  distanceKm?: number;
  sets?: number;
  reps?: number;
  weightKg?: number;
  note?: string;
}

const StatusIcon = ({ entry }: { entry?: IEntry }) => {
  if (!entry || entry.status === "PENDING") {
    return <ScheduleIcon style={{ color: "#bdbdbd" }} fontSize="small" />;
  }
  if (entry.status === "DONE") {
    return <CheckCircleIcon style={{ color: "#4caf50" }} fontSize="small" />;
  }
  return <CancelIcon style={{ color: "#e53935" }} fontSize="small" />;
};

const buildTooltip = (
  intl: IntlShape,
  entry?: IEntry
): string => {
  if (!entry || entry.status === "PENDING") {
    return "";
  }
  if (entry.status === "DONE") {
    const parts = [];
    if (entry.timeMinutes !== undefined && entry.timeMinutes !== null)
      parts.push(`${entry.timeMinutes} min`);
    if (entry.distanceKm !== undefined && entry.distanceKm !== null)
      parts.push(`${entry.distanceKm} km`);
    if (entry.sets !== undefined && entry.sets !== null)
      parts.push(`${entry.sets} sets`);
    if (entry.reps !== undefined && entry.reps !== null)
      parts.push(`${entry.reps} reps`);
    if (entry.weightKg !== undefined && entry.weightKg !== null)
      parts.push(`${entry.weightKg} kg`);
    if (entry.note) parts.push(entry.note);
    return parts.join(" · ") || "OK";
  }
  const reasonLabels = getSkipReasonLabels(intl);
  const reasonLabel = entry.reason
    ? reasonLabels[entry.reason as keyof typeof reasonLabels]
    : "";
  return [reasonLabel, entry.reasonNote].filter(Boolean).join(": ");
};

interface IProgressRow {
  playerId: string;
  playerName: string;
  entriesByDate: Record<string, IEntry>;
}

interface IPreSeasonProgressTableProps {
  exerciseId: string;
  intl: IntlShape;
}

const PreSeasonProgressTable = injectIntl<
  Omit<IPreSeasonProgressTableProps, "intl">
>(({ exerciseId, intl }) => {
  const { data } = useQuery(PRE_SEASON_PROGRESS, {
    variables: { exerciseId },
  });
  const [detailEntry, setDetailEntry] = useState<IEntry | null>(null);

  const progress = data && data.preSeasonProgress;
  if (!progress) {
    return null;
  }

  const dates: string[] = progress.dates;
  const rows: IProgressRow[] = progress.rows.map((row: any) => ({
    playerId: row.player.id,
    playerName: `${row.player.name} ${row.player.surname}`,
    entriesByDate: row.entries.reduce((acc: any, entry: IEntry) => {
      acc[entry.date] = entry;
      return acc;
    }, {}),
  }));

  const columns: Array<Column<IProgressRow>> = [
    {
      title: "",
      field: "playerName",
      render: (row) => row.playerName,
    },
    ...dates.map((date) => ({
      title: moment(date).format("dd DD/MM"),
      field: date,
      sorting: false,
      render: (row: IProgressRow) => {
        const entry = row.entriesByDate[date];
        const tooltip = buildTooltip(intl, entry);
        const icon = <StatusIcon entry={entry} />;
        return (
          <div
            style={{ cursor: entry ? "pointer" : "default", textAlign: "center" }}
            onClick={() => entry && setDetailEntry(entry)}
          >
            {tooltip ? <Tooltip title={tooltip}>{icon}</Tooltip> : icon}
          </div>
        );
      },
    })),
  ];

  return (
    <>
      <MaterialTable
        title={intl.formatMessage(messages.progressFor, {
          title: progress.exercise.title,
        })}
        columns={columns}
        data={rows}
        options={{
          search: false,
          paging: false,
          padding: "dense",
        }}
      />
      <Dialog open={!!detailEntry} onClose={() => setDetailEntry(null)}>
        <DialogTitle>
          {detailEntry && moment(detailEntry.date).format("dddd DD/MM/YYYY")}
        </DialogTitle>
        <DialogContent>
          {detailEntry && buildTooltip(intl, detailEntry)}
        </DialogContent>
      </Dialog>
    </>
  );
});

export default PreSeasonProgressTable;
